import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const TransactionHistory = ({ 
  transactions, 
  searchTerm, 
  setSearchTerm, 
  filterType, 
  setFilterType, 
  dateRange, 
  setDateRange 
}) => {
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success-50 border-success-200';
      case 'pending': return 'text-warning bg-warning-50 border-warning-200';
      case 'processing': return 'text-primary bg-primary-50 border-primary-200';
      case 'failed': return 'text-error bg-error-50 border-error-200';
      default: return 'text-text-secondary bg-secondary-50 border-secondary-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'processing': return 'Loader';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deposit': return 'ArrowDownToLine';
      case 'withdrawal': return 'ArrowUpFromLine';
      case 'transfer': return 'ArrowRightLeft';
      default: return 'Circle';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit': return 'text-success';
      case 'withdrawal': return 'text-error';
      case 'transfer': return 'text-primary';
      default: return 'text-text-secondary';
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'timestamp') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleExport = () => {
    const csvContent = [
      ['Date', 'Type', 'Currency', 'Amount', 'USD Value', 'Status', 'Transaction Hash'].join(','),
      ...transactions.map(tx => [
        formatDate(tx.timestamp),
        tx.type,
        tx.currency,
        tx.amount,
        tx.usdValue,
        tx.status,
        tx.txHash || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transaction-history.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-surface rounded-trading border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h2 className="text-xl font-heading-semibold text-text-primary mb-4 sm:mb-0">
            Transaction History
          </h2>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 bg-secondary-100 text-text-primary px-4 py-2 rounded-trading font-body-medium hover:bg-secondary-200 transition-colors duration-trading-fast"
          >
            <Icon name="Download" size={16} />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              Search
            </label>
            <div className="relative">
              <Icon 
                name="Search" 
                size={16} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search transactions..."
                className="w-full pl-10 pr-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
              <option value="transfer">Transfers</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              Sort By
            </label>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="timestamp-desc">Newest First</option>
              <option value="timestamp-asc">Oldest First</option>
              <option value="amount-desc">Amount (High to Low)</option>
              <option value="amount-asc">Amount (Low to High)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="overflow-x-auto">
        {sortedTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="FileText" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary font-body-normal">No transactions found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-secondary-50 border-b border-border">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-body-medium text-text-secondary">
                  <button
                    onClick={() => handleSort('timestamp')}
                    className="flex items-center space-x-1 hover:text-text-primary"
                  >
                    <span>Date</span>
                    <Icon 
                      name={sortBy === 'timestamp' && sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  </button>
                </th>
                <th className="text-left px-6 py-3 text-sm font-body-medium text-text-secondary">Type</th>
                <th className="text-left px-6 py-3 text-sm font-body-medium text-text-secondary">
                  <button
                    onClick={() => handleSort('amount')}
                    className="flex items-center space-x-1 hover:text-text-primary"
                  >
                    <span>Amount</span>
                    <Icon 
                      name={sortBy === 'amount' && sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                    />
                  </button>
                </th>
                <th className="text-left px-6 py-3 text-sm font-body-medium text-text-secondary">Status</th>
                <th className="text-left px-6 py-3 text-sm font-body-medium text-text-secondary">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-secondary-50 transition-colors duration-trading-fast">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-body-medium text-text-primary">
                        {formatDate(transaction.timestamp)}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={getTypeIcon(transaction.type)} 
                        size={16} 
                        className={getTypeColor(transaction.type)} 
                      />
                      <span className="text-sm font-body-medium text-text-primary capitalize">
                        {transaction.type}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-body-medium text-text-primary">
                        {transaction.amount.toLocaleString()} {transaction.currency}
                      </p>
                      <p className="text-xs text-text-secondary">
                        ≈ ${transaction.usdValue.toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-body-medium border ${getStatusColor(transaction.status)}`}>
                      <Icon name={getStatusIcon(transaction.status)} size={12} />
                      <span className="capitalize">{transaction.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {transaction.txHash && (
                        <p className="text-xs text-text-secondary font-data">
                          {transaction.txHash.slice(0, 10)}...{transaction.txHash.slice(-8)}
                        </p>
                      )}
                      {transaction.confirmations && (
                        <p className="text-xs text-text-secondary">
                          {transaction.confirmations} confirmations
                        </p>
                      )}
                      {transaction.description && (
                        <p className="text-xs text-text-secondary">
                          {transaction.description}
                        </p>
                      )}
                      {transaction.address && (
                        <p className="text-xs text-text-secondary font-data">
                          To: {transaction.address.slice(0, 10)}...{transaction.address.slice(-8)}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;